"use client";

import { useMemo } from "react";
import type { GpxTrackPoint } from "./types";
import { useTheme } from "@/features/theme";

interface ElevationProfileProps {
  trkpts: GpxTrackPoint[];   // full-resolution track (pre-trim, for scale)
  displayTrkpts: GpxTrackPoint[]; // trimmed + simplified (what to draw)
  trimStart: number;         // 0–1
  trimEnd: number;           // 0–1
  isDark: boolean;
}

function fmtM(m: number): string {
  return `${Math.round(m)} m`;
}

export default function ElevationProfile({
  trkpts,
  displayTrkpts,
  trimStart,
  trimEnd,
  isDark: isDarkProp,
}: ElevationProfileProps) {
  const { isDark: themeIsDark } = useTheme();
  const isDark = isDarkProp ?? themeIsDark;
  // Only render when we have elevation data
  const hasEle = trkpts.some((p) => p.ele != null);
  if (!hasEle || displayTrkpts.length < 2) return null;

  const { path, minEle, maxEle, totalDistM } = useMemo(() => {
    const pts = displayTrkpts.filter((p) => p.ele != null) as (GpxTrackPoint & { ele: number })[];
    if (pts.length < 2) return { path: "", minEle: 0, maxEle: 0, totalDistM: 0 };

    const eles = pts.map((p) => p.ele);
    const minEle = Math.min(...eles);
    const maxEle = Math.max(...eles);
    const totalDistM = pts[pts.length - 1].distFromStart;
    const eleRange = Math.max(maxEle - minEle, 1);

    const W = 280; // SVG viewBox width
    const H = 56;  // SVG viewBox height
    const PAD = 2;

    const points = pts.map((p) => {
      const x = PAD + ((p.distFromStart / totalDistM) * (W - 2 * PAD));
      const y = H - PAD - ((p.ele - minEle) / eleRange) * (H - 2 * PAD);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    });

    const path =
      `M ${points[0]} ` +
      points.slice(1).map((p) => `L ${p}`).join(" ") +
      ` L ${(PAD + (W - 2 * PAD)).toFixed(1)},${H} L ${PAD},${H} Z`;

    return { path, minEle, maxEle, totalDistM };
  }, [displayTrkpts]);

  if (!path) return null;

  const fillColor  = isDark ? "rgba(99,102,241,0.35)"  : "rgba(99,102,241,0.2)";
  const strokeColor = isDark ? "rgba(99,102,241,0.8)" : "rgba(99,102,241,0.9)";
  const labelColor  = isDark ? "#71717a" : "#a1a1aa"; // zinc-500/400
  const bgColor     = isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)";

  // Trim window overlay
  const trimW = (trimEnd - trimStart) * 280;
  const trimX = trimStart * 280;

  return (
    <div className={`rounded-xl overflow-hidden border border-ink/5`} style={{ background: bgColor }}>
      <svg viewBox="0 0 280 56" width="100%" height="56" preserveAspectRatio="none">
        {/* Trim window highlight */}
        {(trimStart > 0 || trimEnd < 1) && (
          <rect
            x={trimX}
            y={0}
            width={trimW}
            height={56}
            fill="rgba(99,102,241,0.08)"
          />
        )}
        {/* Elevation fill */}
        <path d={path} fill={fillColor} />
        {/* Elevation stroke */}
        <path
          d={path.split(" L")[0] + " " + path.split(" L").slice(1, -2).map((s) => `L ${s}`).join(" ")}
          fill="none"
          stroke={strokeColor}
          strokeWidth="1.5"
        />
      </svg>

      {/* Labels */}
      <div className="flex justify-between px-2 pb-1.5 -mt-0.5">
        <span className="text-[9px]" style={{ color: labelColor }}>{fmtM(minEle)}</span>
        <span className="text-[9px]" style={{ color: labelColor }}>{fmtM(maxEle)}</span>
      </div>
    </div>
  );
}
